import { useState, useEffect, createContext, useContext } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSiteContentCache, SiteLoadingScreen } from "@/contexts/SiteContentCache";
import TranslateWidget from "@/components/TranslateWidget";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { 
  Menu, 
  X, 
  ChevronRight, 
  Users, 
  Trophy, 
  Heart, 
  Mail, 
  Phone, 
  MapPin,
  Instagram,
  Facebook,
  Youtube,
  Twitter,
  Globe,
  ChevronDown
} from "lucide-react";

// Fallback defaults (used until API loads)
const DEFAULTS = {
  logo_url: "https://customer-assets.emergentagent.com/job_crm-news-hub/artifacts/srv2lps9_ChatGPT%20Image%20Apr%202%2C%202026%2C%2010_47_10%20AM.png",
  logo_fit: "contain",
  sections: [
    { key: "quick_links", label: "Quick Links Bar", enabled: true, order: 0 },
    { key: "hero", label: "Hero", enabled: true, order: 1 },
    { key: "features", label: "Features", enabled: true, order: 2 },
    { key: "why", label: "Why Commonwealth", enabled: true, order: 3 },
    { key: "teams", label: "Teams", enabled: true, order: 4 },
    { key: "philosophy", label: "Philosophy / Stats", enabled: true, order: 5 },
    { key: "news", label: "News & Updates", enabled: true, order: 6 },
    { key: "cta", label: "Call to Action", enabled: true, order: 7 },
    { key: "sponsors", label: "Sponsors Bar", enabled: true, order: 8 },
  ],
  hero: {
    title: "COMMONWEALTH FC",
    tagline: "Built Different. Built Together.",
    button_primary: "JOIN A TRAINING SESSION",
    button_secondary: "LEARN ABOUT THE CLUB",
    image_url: "https://images.unsplash.com/photo-1720190368909-ad3eb02383b7?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200",
    image_fit: "cover"
  },
  features: [
    { icon: "diamond-outline", title: "PLAYER DEVELOPMENT FIRST", description: "We treat our playmakers right. At Commonwealth FC, we're committed to developing your player's fitness." },
    { icon: "diamond-fill", title: "CLEAR PATHWAY & COMPETITION", description: "We aim to be your player's permanent home. Compete at your level while growing your game, coming your expectations." },
    { icon: "diamond-small", title: "A CLUB THAT ACTUALLY CARES", description: "We keep at the young player's interests first. FC values family/team/self, allowing your Pressure." }
  ],
  why: {
    heading: "WHY COMMONWEALTH?",
    paragraphs: [
      "Where you play matters. At Commonwealth FC, we're committed to elevating youth soccer. Our goal is to develop talented, motivated players — not just fill rosters.",
      "We believe in development over politics. Every player deserves quality coaching, meaningful playing time, and a pathway to reach their potential."
    ],
    button_text: "APPLY FOR A FREE EVALUATION",
    image1_url: "https://static.prod-images.emergentagent.com/jobs/55952a6b-5c34-41c4-a4af-2292c177e289/images/082b436f03611fb73e060887f592badbcc9e89ce79418d6f4ea062b24c4dfa36.png",
    image2_url: "https://static.prod-images.emergentagent.com/jobs/55952a6b-5c34-41c4-a4af-2292c177e289/images/fd941e5b85555d226a892e124400617831610adae38a50f85a3f5a19462883b9.png",
    image1_fit: "cover",
    image2_fit: "cover"
  },
  teams: [
    { title: "YOUTH ACADEMY", ages: "U8 - U11", description: "Foundation training focused on technical skills and love of the game" },
    { title: "DEVELOPMENT", ages: "U12 - U15", description: "Competitive play with emphasis on tactical understanding and physical development" },
    { title: "ELITE", ages: "U16 - U19", description: "High-level competition preparing players for college and beyond" }
  ],
  teams_heading: "OUR TEAMS",
  teams_subtext: "Commonwealth FC offers competitive teams across all age groups, providing pathways for players at every level.",
  philosophy: {
    heading: "DEVELOPMENT OVER POLITICS",
    body: "At Commonwealth FC, we believe every player deserves an environment where they can thrive. We prioritize player development, meaningful competition, and creating pathways to success — not club politics or pay-to-play schemes.",
    stats: [
      { stat: "100%", label: "Licensed Coaches" },
      { stat: "15+", label: "Teams" },
      { stat: "500+", label: "Players Developed" }
    ]
  },
  cta: {
    subtext: "NOT SATISFIED WITH YOUR CURRENT CLUB?",
    heading: "Join Commonwealth FC",
    button_primary: "APPLY OUR TEAMS",
    button_secondary: "ABOUT OUR TEAMS"
  },
  footer: {
    email: "info@commonwealthfc.com",
    phone: "(555) 123-4567",
    location: "Virginia, USA",
    instagram: "",
    twitter: "",
    facebook: ""
  }
};

const SiteContentContext = createContext(DEFAULTS);
const useSiteContent = () => useContext(SiteContentContext);

// Shared helper for image fit CSS classes
const imageFitClass = (fit, heightClass = "h-64") => {
  switch (fit) {
    case "contain": return `max-h-[480px] object-contain`;
    case "fill": return `${heightClass} object-fill`;
    case "auto": return `max-h-[480px] object-scale-down`;
    default: return `${heightClass} object-cover`;
  }
};

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const content = useSiteContent();
  const logoSrc = content.logo_url || DEFAULTS.logo_url;
  const logoFit = content.logo_fit || "contain";
  const { lang, setLang, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsOpen(false);
  };

  const navLinks = [
    { label: "About", id: "about" },
    { label: "Teams", id: "teams" },
    { label: "Philosophy", id: "philosophy" },
    { label: "Contact", id: "contact" },
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#0C2E3C] shadow-lg" : "bg-[#0C2E3C]/95"
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img src={logoSrc} alt="Commonwealth FC" className={`h-9 w-auto object-${logoFit}`} />
            <span className="text-sm font-bold uppercase tracking-wider text-[#F0F4F3] hidden sm:block">
              {content.hero?.title || "Commonwealth FC"}
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-5">
            {navLinks.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-[#F0F4F3]/90 hover:text-[#C8E64E] font-medium text-sm tracking-wider transition-colors"
                data-testid={`nav-${item.id}`}
              >
                {item.label}
              </button>
            ))}
            {/* Tryouts & Events dropdown */}
            <div className="relative group">
              <button className="text-[#F0F4F3]/90 hover:text-[#C8E64E] font-medium text-sm tracking-wider transition-colors flex items-center gap-1" data-testid="nav-tryouts-events">
                Tryouts & Events <ChevronDown size={14} />
              </button>
              <div className="absolute right-0 top-full pt-2 hidden group-hover:block z-50">
                <div className="bg-[#0C2E3C] border border-[#F0F4F3]/10 shadow-xl min-w-[180px] py-1">
                  <Link to="/tryouts" className="block px-4 py-2.5 text-sm text-[#F0F4F3]/80 hover:text-[#C8E64E] hover:bg-[#F0F4F3]/5 transition-colors" data-testid="nav-tryouts">Tryouts</Link>
                  <Link to="/events" className="block px-4 py-2.5 text-sm text-[#F0F4F3]/80 hover:text-[#C8E64E] hover:bg-[#F0F4F3]/5 transition-colors" data-testid="nav-events">Events Calendar</Link>
                </div>
              </div>
            </div>

            {/* Club Info dropdown */}
            <div className="relative group">
              <button className="text-[#F0F4F3]/90 hover:text-[#C8E64E] font-medium text-sm tracking-wider transition-colors flex items-center gap-1" data-testid="nav-club-info">
                Club Info <ChevronDown size={14} />
              </button>
              <div className="absolute right-0 top-full pt-2 hidden group-hover:block z-50">
                <div className="bg-[#0C2E3C] border border-[#F0F4F3]/10 shadow-xl min-w-[180px] py-1">
                  <Link to="/coaches" className="block px-4 py-2.5 text-sm text-[#F0F4F3]/80 hover:text-[#C8E64E] hover:bg-[#F0F4F3]/5 transition-colors" data-testid="nav-coaches">Bring Your Team</Link>
                  <Link to="/highlights" className="block px-4 py-2.5 text-sm text-[#F0F4F3]/80 hover:text-[#C8E64E] hover:bg-[#F0F4F3]/5 transition-colors" data-testid="nav-highlights">Player Highlights</Link>
                  <Link to="/sponsors" className="block px-4 py-2.5 text-sm text-[#F0F4F3]/80 hover:text-[#C8E64E] hover:bg-[#F0F4F3]/5 transition-colors" data-testid="nav-sponsors">Our Sponsors</Link>
                  <Link to="/map" className="block px-4 py-2.5 text-sm text-[#F0F4F3]/80 hover:text-[#C8E64E] hover:bg-[#F0F4F3]/5 transition-colors" data-testid="nav-map">Our Community</Link>
                  <Link to="/referral" className="block px-4 py-2.5 text-sm text-[#F0F4F3]/80 hover:text-[#C8E64E] hover:bg-[#F0F4F3]/5 transition-colors" data-testid="nav-referral">Refer a Friend</Link>
                  <Link to="/livestream" className="block px-4 py-2.5 text-sm text-[#F0F4F3]/80 hover:text-[#C8E64E] hover:bg-[#F0F4F3]/5 transition-colors" data-testid="nav-livestream">Live Streams</Link>
                  <Link to="/faq" className="block px-4 py-2.5 text-sm text-[#F0F4F3]/80 hover:text-[#C8E64E] hover:bg-[#F0F4F3]/5 transition-colors" data-testid="nav-faq">FAQ</Link>
                </div>
              </div>
            </div>

            <TranslateWidget />
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-[#F0F4F3]"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden bg-[#0C2E3C] border-t border-[#F0F4F3]/20 py-4 overflow-x-hidden">
            {navLinks.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="block w-full text-left py-3 px-4 text-[#F0F4F3]/90 hover:text-[#C8E64E] font-medium tracking-wider"
              >
                {item.label}
              </button>
            ))}
            <div className="border-t border-[#F0F4F3]/10 my-2 mx-4" />
            <p className="px-4 py-1 text-[10px] text-[#F0F4F3]/30 uppercase tracking-widest font-bold">Tryouts & Events</p>
            <Link to="/tryouts" className="block w-full text-left py-2.5 px-4 text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm tracking-wider" onClick={() => setIsOpen(false)} data-testid="mobile-nav-tryouts">Tryouts</Link>
            <Link to="/events" className="block w-full text-left py-2.5 px-4 text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm tracking-wider" onClick={() => setIsOpen(false)} data-testid="mobile-nav-events">Events Calendar</Link>
            <div className="border-t border-[#F0F4F3]/10 my-2 mx-4" />
            <p className="px-4 py-1 text-[10px] text-[#F0F4F3]/30 uppercase tracking-widest font-bold">Club Info</p>
            <Link to="/coaches" className="block w-full text-left py-2.5 px-4 text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm tracking-wider" onClick={() => setIsOpen(false)} data-testid="mobile-nav-coaches">Bring Your Team</Link>
            <Link to="/highlights" className="block w-full text-left py-2.5 px-4 text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm tracking-wider" onClick={() => setIsOpen(false)} data-testid="mobile-nav-highlights">Player Highlights</Link>
            <Link to="/sponsors" className="block w-full text-left py-2.5 px-4 text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm tracking-wider" onClick={() => setIsOpen(false)} data-testid="mobile-nav-sponsors">Our Sponsors</Link>
            <Link to="/map" className="block w-full text-left py-2.5 px-4 text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm tracking-wider" onClick={() => setIsOpen(false)} data-testid="mobile-nav-map">Our Community</Link>
            <Link to="/referral" className="block w-full text-left py-2.5 px-4 text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm tracking-wider" onClick={() => setIsOpen(false)} data-testid="mobile-nav-referral">Refer a Friend</Link>
            <Link to="/livestream" className="block w-full text-left py-2.5 px-4 text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm tracking-wider" onClick={() => setIsOpen(false)} data-testid="mobile-nav-livestream">Live Streams</Link>
            <Link to="/faq" className="block w-full text-left py-2.5 px-4 text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm tracking-wider" onClick={() => setIsOpen(false)} data-testid="mobile-nav-faq">FAQ</Link>
            <div className="border-t border-[#F0F4F3]/10 my-2 mx-4" />
            <Link 
              to="/join"
              className="block w-full text-left py-3 px-4 text-[#C8E64E] font-bold tracking-wider"
            >
              JOIN NOW
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

const HeroSection = () => {
  const { hero } = useSiteContent();
  return (
    <section 
      className="relative bg-[#0C2E3C]"
      data-testid="hero-section"
    >
      {/* Background texture overlay */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 items-center pt-20 pb-10">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F0F4F3] mb-3 tracking-tight">
              {hero.title}
            </h1>
            <p className="text-lg sm:text-xl text-[#F0F4F3]/80 mb-6 italic">
              {hero.tagline}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link to="/join">
                <Button 
                  className="bg-transparent border-2 border-[#F0F4F3] text-[#F0F4F3] hover:bg-[#F0F4F3] hover:text-[#0C2E3C] font-bold text-sm uppercase tracking-wider px-8 h-14 rounded-none"
                  data-testid="hero-join-btn"
                >
                  {hero.button_primary}
                </Button>
              </Link>
              <Button 
                onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })}
                variant="ghost"
                className="border-2 border-[#F0F4F3] text-[#F0F4F3] hover:bg-[#F0F4F3] hover:text-[#0C2E3C] font-bold text-sm uppercase tracking-wider px-8 h-14 rounded-none"
                data-testid="hero-learn-btn"
              >
                {hero.button_secondary}
              </Button>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative">
            <img 
              src={hero.image_url} 
              alt="Youth soccer players" 
              className={`w-full rounded-sm ${imageFitClass(hero.image_fit, "h-[320px] lg:h-[420px]")}`}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const ICON_MAP = { "diamond-outline": "◇", "diamond-fill": "◈", "diamond-small": "♦" };

const FeaturesSection = () => {
  const { features } = useSiteContent();
  return (
    <section className="py-14 bg-[#F0F4F3]" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div 
              key={`feature-${feature.title}-${index}`}
              className="text-center p-8"
              data-testid={`feature-card-${index}`}
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border-2 border-[#0C2E3C] mb-6">
                <span className="text-2xl text-[#0C2E3C]">{ICON_MAP[feature.icon] || "◇"}</span>
              </div>
              <h3 className="font-bold text-base uppercase tracking-wider text-[#0C2E3C] mb-4">
                {feature.title}
              </h3>
              <p className="text-[#6B8A8F] text-sm leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhySection = () => {
  const { why } = useSiteContent();
  return (
    <section id="about" className="py-24 bg-[#F0F4F3]" data-testid="why-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[#0C2E3C] mb-6">
              {why.heading}
            </h2>
            {why.paragraphs.map((p, i) => (
              <p key={`why-p-${i}`} className="text-[#6B8A8F] mb-6 leading-relaxed">{p}</p>
            ))}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/join">
                <Button 
                  className="bg-transparent border-2 border-[#0C2E3C] text-[#0C2E3C] hover:bg-[#0C2E3C] hover:text-[#F0F4F3] font-bold text-sm uppercase tracking-wider px-8 h-14 rounded-none"
                  data-testid="why-apply-btn"
                >
                  {why.button_text}
                </Button>
              </Link>
              <Link to="/coaches">
                <Button 
                  className="bg-[#C8E64E] hover:bg-[#B5D33E] text-[#0C2E3C] font-bold text-sm uppercase tracking-wider px-8 h-14 rounded-none"
                  data-testid="why-coaches-btn"
                >
                  Have a Team You Want to Bring Over?
                </Button>
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img 
              src={why.image1_url} 
              alt="Youth training" 
              className={`w-full ${imageFitClass(why.image1_fit, "h-64")}`}
            />
            <img 
              src={why.image2_url} 
              alt="Team huddle" 
              className={`w-full mt-8 ${imageFitClass(why.image2_fit, "h-64")}`}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const TeamsSection = () => {
  const content = useSiteContent();
  return (
    <section id="teams" className="py-24 bg-[#0C2E3C]" data-testid="teams-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[#F0F4F3] mb-4 text-center">
          {content.teams_heading}
        </h2>
        <p className="text-center text-[#F0F4F3]/70 mb-12 max-w-2xl mx-auto">
          {content.teams_subtext}
        </p>
        
        <div className="grid md:grid-cols-3 gap-6">
          {content.teams.map((team) => (
            <div 
              key={team.title} 
              className="border border-[#F0F4F3]/30 p-8 text-center hover:border-[#C8E64E] transition-colors"
              data-testid={`team-card-${team.title}`}
            >
              <h3 className="font-bold text-lg text-[#F0F4F3] mb-2">{team.title}</h3>
              <p className="text-[#C8E64E] font-semibold text-sm mb-4">{team.ages}</p>
              <p className="text-[#F0F4F3]/70 text-base">{team.description}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/join">
            <Button 
              className="bg-[#C8E64E] hover:bg-[#B5D33E] text-[#0C2E3C] font-bold text-sm uppercase tracking-wider px-8 h-14 rounded-none"
            >
              View All Teams & Register
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

const PhilosophySection = () => {
  const { philosophy } = useSiteContent();
  return (
    <section id="philosophy" className="py-20 bg-[#0C2E3C]" data-testid="philosophy-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[#F0F4F3] mb-8">
            {philosophy.heading}
          </h2>
          <p className="text-[#F0F4F3]/70 text-lg mb-8 leading-relaxed">
            {philosophy.body}
          </p>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {philosophy.stats.map((item) => (
              <div key={item.label}>
                <p className="font-heading text-4xl font-bold text-[#C8E64E]">{item.stat}</p>
                <p className="text-[#F0F4F3]/60 text-sm mt-2">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const CTASection = () => {
  const { cta } = useSiteContent();
  const { t } = useLanguage();
  return (
    <section className="py-12 bg-[#0C2E3C] border-t border-[#F0F4F3]/20" data-testid="cta-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <p className="text-[#F0F4F3]/70 text-base mb-2">{cta.subtext}</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#F0F4F3]">
              {cta.heading}
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/join">
              <Button 
                className="bg-transparent border-2 border-[#F0F4F3] text-[#F0F4F3] hover:bg-[#F0F4F3] hover:text-[#0C2E3C] font-bold text-sm uppercase tracking-wider px-8 h-14 rounded-none"
                data-testid="cta-apply-btn"
              >
                {cta.button_primary}
              </Button>
            </Link>
            <Button 
              onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-transparent border-2 border-[#F0F4F3] text-[#F0F4F3] hover:bg-[#F0F4F3] hover:text-[#0C2E3C] font-bold text-sm uppercase tracking-wider px-8 h-14 rounded-none"
            >
              {cta.button_secondary}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

const NewsSection = () => {
  const [news, setNews] = useState([]);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API}/news/public`);
        setNews(response.data);
      } catch (error) {
        console.error("Failed to fetch news:", error);
      }
    };
    fetchNews();
  }, []); // API and axios are stable module-level constants

  if (news.length === 0) {
    return null;
  }

  return (
    <section className="py-20 bg-[#F0F4F3]" data-testid="news-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-heading text-4xl font-bold text-[#0C2E3C] mb-12 text-center">
          NEWS & UPDATES
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {news.slice(0, 3).map((post) => (
            <Link
              to={`/news/${post.post_id}`}
              key={post.post_id}
              className="group border border-[#0C2E3C]/20 hover:border-[#0C2E3C] transition-colors block"
              data-testid={`news-card-${post.post_id}`}
            >
              {post.image_id && (
                <img
                  src={`${API}/files/${post.image_id}`}
                  alt={post.title}
                  className={`w-full ${imageFitClass(post.image_fit, "h-40")}`}
                />
              )}
              <div className="p-6">
                <p className="text-xs text-[#6B8A8F] mb-2">
                  {new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </p>
                <h3 className="font-bold text-lg text-[#0C2E3C] mb-3 group-hover:text-[#C8E64E] transition-colors">
                  {post.title}
                </h3>
                <p className="text-[#6B8A8F] text-base mb-4 line-clamp-3">
                  {post.content}
                </p>
                <span className="text-[#0C2E3C] font-medium text-sm group-hover:text-[#C8E64E] transition-colors">
                  Read More →
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const QuickLinksBar = () => {
  const [links, setLinks] = useState([]);

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const response = await axios.get(`${API}/quick-links/public`);
        setLinks(response.data);
      } catch (error) {
        console.error("Failed to fetch quick links:", error);
      }
    };
    fetchLinks();
  }, []);

  if (links.length === 0) return null;

  return (
    <div className="bg-[#C8E64E] py-2.5 overflow-hidden" data-testid="quick-links-bar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center gap-4 sm:gap-8 flex-nowrap overflow-x-auto whitespace-nowrap no-scrollbar">
          {links.map((link) => {
            const isInternal = link.url.startsWith("/");
            return isInternal ? (
              <Link
                key={link.link_id}
                to={link.url}
                className="text-[#0C2E3C] font-bold text-xs sm:text-sm tracking-wide hover:underline whitespace-nowrap"
                data-testid={`quick-link-${link.link_id}`}
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.link_id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0C2E3C] font-bold text-xs sm:text-sm tracking-wide hover:underline whitespace-nowrap"
                data-testid={`quick-link-${link.link_id}`}
              >
                {link.label}
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const SponsorsBar = () => {
  const [sponsors, setSponsors] = useState([]);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const response = await axios.get(`${API}/sponsors/public`);
        setSponsors(response.data);
      } catch (error) {
        console.error("Failed to fetch sponsors:", error);
      }
    };
    fetchSponsors();
  }, []); // API and axios are stable module-level constants

  if (sponsors.length === 0) return null;

  return (
    <section className="py-10 bg-[#F0F4F3] border-t border-[#0C2E3C]/10" data-testid="sponsors-bar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-center text-[#6B8A8F] text-sm font-semibold uppercase tracking-widest mb-6">
          Our Partners & Sponsors
        </p>
        <div className="flex items-center justify-center gap-8 sm:gap-12 flex-wrap">
          {sponsors.map((sponsor) => (
            <a
              key={sponsor.sponsor_id}
              href={sponsor.website_url || "#"}
              target={sponsor.website_url ? "_blank" : "_self"}
              rel="noopener noreferrer"
              className="opacity-60 hover:opacity-100 transition-opacity"
              data-testid={`sponsor-logo-${sponsor.sponsor_id}`}
            >
              <img
                src={sponsor.logo_url}
                alt={sponsor.name}
                className="h-8 sm:h-10 w-auto object-contain grayscale hover:grayscale-0 transition-all"
              />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  const { footer: ft, hero, logo_url, logo_fit } = useSiteContent();
  const logoSrc = logo_url || DEFAULTS.logo_url;
  const footerLogoFit = logo_fit || "contain";
  return (
    <footer id="contact" className="bg-[#0C2E3C] py-8 border-t border-[#F0F4F3]/10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Logo & About */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <img src={logoSrc} alt="Commonwealth FC" className={`h-10 w-auto object-${footerLogoFit}`} />
              <div>
                <h3 className="font-bold text-lg text-[#F0F4F3]">{hero.title}</h3>
                <p className="text-[#F0F4F3]/50 text-sm mt-0.5">{hero.tagline}</p>
              </div>
            </div>
            <p className="text-[#F0F4F3]/60 text-base mb-4 max-w-sm">
              Developing talented youth soccer players through quality coaching and a family-first approach.
            </p>
            <div className="flex gap-4">
              {ft.instagram && (
                <a href={ft.instagram} target="_blank" rel="noopener noreferrer" className="text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors" aria-label="Instagram">
                  <Instagram size={20} />
                </a>
              )}
              {ft.twitter && (
                <a href={ft.twitter} target="_blank" rel="noopener noreferrer" className="text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors" aria-label="Twitter">
                  <Twitter size={20} />
                </a>
              )}
              {ft.facebook && (
                <a href={ft.facebook} target="_blank" rel="noopener noreferrer" className="text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors" aria-label="Facebook">
                  <Facebook size={20} />
                </a>
              )}
              {!ft.instagram && !ft.twitter && !ft.facebook && (
                <>
                  <a href="#" className="text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors" aria-label="Instagram"><Instagram size={20} /></a>
                  <a href="#" className="text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors" aria-label="Twitter"><Twitter size={20} /></a>
                  <a href="#" className="text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors" aria-label="Facebook"><Facebook size={20} /></a>
                </>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider text-[#F0F4F3] mb-4">Contact</h4>
            <div className="space-y-3 text-sm">
              <a href={`mailto:${ft.email}`} className="flex items-center gap-2 text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors">
                <Mail size={16} />
                {ft.email}
              </a>
              <a href={`tel:${ft.phone}`} className="flex items-center gap-2 text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors">
                <Phone size={16} />
                {ft.phone}
              </a>
              <p className="flex items-center gap-2 text-[#F0F4F3]/60">
                <MapPin size={16} />
                {ft.location}
              </p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider text-[#F0F4F3] mb-4">Quick Links</h4>
            <div className="space-y-2 text-sm">
              <button 
                onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })}
                className="block text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors"
              >
                About Us
              </button>
              <button 
                onClick={() => document.getElementById("teams")?.scrollIntoView({ behavior: "smooth" })}
                className="block text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors"
              >
                Teams
              </button>
              <Link 
                to="/join"
                className="block text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors"
              >
                Register
              </Link>
              <Link 
                to="/coaches"
                className="block text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors"
              >
                Bring Your Team
              </Link>
              <Link 
                to="/livestream"
                className="block text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors"
                data-testid="footer-livestream-link"
              >
                Live Streams
              </Link>
              <Link 
                to="/admin/login"
                className="block text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors"
                data-testid="footer-admin-link"
              >
                Admin Login
              </Link>
            </div>
          </div>
        </div>

        <div className="border-t border-[#F0F4F3]/10 mt-6 pt-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-[#F0F4F3]/40 text-sm">
          <span>&copy; {new Date().getFullYear()} Commonwealth FC. All rights reserved.</span>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-[#C8E64E] transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#C8E64E] transition-colors" data-testid="footer-terms-link">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

const SECTION_COMPONENTS = {
  quick_links: QuickLinksBar,
  hero: HeroSection,
  features: FeaturesSection,
  why: WhySection,
  teams: TeamsSection,
  philosophy: PhilosophySection,
  news: NewsSection,
  cta: CTASection,
  sponsors: SponsorsBar,
};

const HomePage = () => {
  const { data: cachedContent, loading: cacheLoading } = useSiteContentCache();
  const [seo, setSeo] = useState({});

  useEffect(() => {
    axios.get(`${API}/seo/public`).then(res => {
      if (res?.data) setSeo(res.data);
    }).catch(() => {});
  }, []);

  if (!cachedContent && cacheLoading) return <SiteLoadingScreen />;

  const content = cachedContent ? { ...DEFAULTS, ...cachedContent } : DEFAULTS;

  const sections = (content.sections || DEFAULTS.sections)
    .filter((s) => s.enabled)
    .sort((a, b) => a.order - b.order);

  return (
    <SiteContentContext.Provider value={content}>
      <Helmet>
        <title>{seo.site_title || "Commonwealth FC | Youth Soccer Club in Charlottesville, VA"}</title>
        <meta name="description" content={seo.site_description || "Commonwealth FC is Charlottesville's community-first youth soccer club. Competitive teams for ages U8-U19, licensed coaches, and affordable registration. Nil Desperandum."} />
        <meta name="keywords" content={seo.keywords || "youth soccer Charlottesville, soccer club Virginia, Commonwealth FC, travel soccer, kids soccer Charlottesville, Albemarle County soccer"} />
        <meta property="og:title" content={seo.site_title || "Commonwealth FC | Youth Soccer Club"} />
        <meta property="og:description" content={seo.site_description || "Charlottesville's community-first youth soccer club. Competitive teams U8-U19."} />
        {seo.og_image && <meta property="og:image" content={seo.og_image} />}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://commonwealthfc.org" />
        <meta property="og:site_name" content="Commonwealth FC" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.site_title || "Commonwealth FC"} />
        <meta name="twitter:description" content={seo.site_description || "Charlottesville's community-first youth soccer club."} />
        <link rel="canonical" href="https://commonwealthfc.org" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": ["SportsOrganization", "LocalBusiness"],
            "name": "Commonwealth FC",
            "alternateName": "Commonwealth Football Club",
            "description": "Community-first youth soccer club in Charlottesville, Virginia. Competitive travel teams for ages U8-U19 with licensed coaches and affordable registration.",
            "url": "https://commonwealthfc.org",
            "sport": "Soccer",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Charlottesville",
              "addressRegion": "VA",
              "addressCountry": "US"
            },
            "areaServed": [
              {"@type": "City", "name": "Charlottesville"},
              {"@type": "AdministrativeArea", "name": "Albemarle County"}
            ],
            "foundingDate": "2017",
            "slogan": "Built Different. Built Together.",
            "keywords": "youth soccer, travel soccer, competitive soccer, Charlottesville VA",
            "memberOf": [
              {"@type": "SportsOrganization", "name": "US Club Soccer"},
              {"@type": "SportsOrganization", "name": "Virginia Youth Soccer Association"}
            ]
          }
        `}</script>
      </Helmet>
      <div className="min-h-screen">
        <Navigation />
        <div className="pt-14">
          {sections.map((s) => {
            const Component = SECTION_COMPONENTS[s.key];
            return Component ? <Component key={s.key} /> : null;
          })}
        </div>
        <Footer />
      </div>
    </SiteContentContext.Provider>
  );
};

export default HomePage;
